import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { shopProducts, shopCategories } from '../data/shopData';
import ProductCard from './ProductCard';
import SafeIcon from '../common/SafeIcon';
import { FiZap, FiTarget, FiAward, FiClock, FiShield, FiArrowDown, FiStar, FiChevronRight } from 'react-icons/fi';
import { useCountdown } from '../hooks/useCountdown';
import { Link } from 'react-router-dom';
import SchedulingForm from './SchedulingForm';
import StepProcess from './StepProcess';

const WorkshopLanding = () => {
  const { hours, minutes, seconds } = useCountdown(48, 0, 0);
  const [currency, setCurrency] = useState('MXN');

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-20">
      <Helmet>
        <title>Workshop Business English 2026 | Evento de Inscripción Total</title>
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-[4rem] p-8 md:p-16 backdrop-blur-3xl shadow-2xl">
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-3 bg-red-600 px-6 py-2 rounded-full font-black text-[10px] uppercase tracking-[0.4em] mb-10 shadow-xl animate-pulse">
                <SafeIcon icon={FiZap} /> Live Event: 2026 Season
              </div>
              <h1 className="text-5xl md:text-9xl font-black uppercase italic leading-none tracking-tighter mb-10">
                Workshop <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600">
                  Business English
                </span>
              </h1>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-5xl mb-12">
                <div className="bg-black/50 p-6 rounded-3xl border border-zinc-800">
                  <p className="text-[9px] font-black text-gray-500 uppercase mb-2">Inicia en</p>
                  <p className="text-xl font-black text-yellow-500">{hours}:{minutes}:{seconds}</p>
                </div>
                <div className="bg-black/50 p-6 rounded-3xl border border-zinc-800">
                  <p className="text-[9px] font-black text-gray-500 uppercase mb-2">Cupos Libres</p>
                  <p className="text-xl font-black text-green-500">12 / 50</p>
                </div>
                <div className="bg-black/50 p-6 rounded-3xl border border-zinc-800">
                  <p className="text-[9px] font-black text-gray-500 uppercase mb-2">Nivel</p>
                  <p className="text-xl font-black text-blue-500">PRO-PLUS</p>
                </div>
                <div className="bg-black/50 p-6 rounded-3xl border border-zinc-800">
                  <p className="text-[9px] font-black text-gray-500 uppercase mb-2">Plataforma</p>
                  <p className="text-xl font-black text-red-500">CAMPUS 2.0</p>
                </div>
              </div>
              <p className="text-xl md:text-3xl text-gray-400 max-w-4xl font-medium italic leading-relaxed mb-12">
                "Este Workshop es la puerta de entrada. Al inscribirte, no solo asistes al evento, sino que activas tu cuenta para cualquier programa profesional."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVATION FLOW - INTEGRATED IN WORKSHOP */}
      <section className="py-24 px-4 bg-zinc-950">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black uppercase italic">Inscripción al <span className="text-red-500">Workshop</span></h2>
            <p className="text-gray-500 mt-2">Sigue estos pasos para asegurar tu lugar.</p>
          </div>
          <StepProcess />
          <SchedulingForm />
        </div>
      </section>

      {/* THE UNLIMITED SCROLL CATALOG */}
      <section id="catalog" className="py-24 px-4 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
            <div>
              <h2 className="text-4xl md:text-7xl font-black uppercase italic tracking-tighter">
                Módulos de <span className="text-yellow-500">Inmersión</span>
              </h2>
            </div>
            <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-full p-1 shadow-xl">
              <button onClick={() => setCurrency('MXN')} className={`px-10 py-3 rounded-full text-[10px] font-black transition-all ${currency === 'MXN' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>MXN</button>
              <button onClick={() => setCurrency('USD')} className={`px-10 py-3 rounded-full text-[10px] font-black transition-all ${currency === 'USD' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>USD</button>
            </div>
          </div>

          {shopCategories.map((cat) => (
            <div key={cat.id} className="mb-32">
              <div className="flex items-center gap-6 mb-12 group">
                <div className="w-20 h-20 bg-zinc-900 rounded-3xl flex items-center justify-center text-yellow-500 border border-zinc-800 group-hover:border-yellow-500/50 transition-all">
                  <SafeIcon icon={cat.icon} className="text-4xl" />
                </div>
                <div>
                  <h3 className="text-3xl md:text-5xl font-black uppercase italic tracking-tight">{cat.name}</h3>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {shopProducts.filter(p => p.category === cat.id).map(product => (
                  <ProductCard key={product.id} product={product} currency={currency} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default WorkshopLanding;